/**
 * Lazy loads sections and images using IntersectionObserver
 * Improves initial page load performance by deferring below-the-fold content
 */

(function() {
  'use strict';

  // Configuration
  const ROOT_MARGIN = '0px'; // No margin - trigger exactly when section enters
  const THRESHOLD = 0.25; // Trigger when 25% of previous section is scrolled through

  /**
   * Lazy load images within a section
   */
  function lazyLoadSectionImages(section) {
    // Find all images with data-src attributes (true lazy loading)
    const lazyImages = section.querySelectorAll('img[data-src]');
    
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.sizes) img.sizes = img.dataset.sizes;
        img.style.opacity = '1';
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-sizes');
      }
    });

    // Find images with loading="lazy" that haven't loaded yet
    const lazyNativeImages = section.querySelectorAll('img[loading="lazy"]:not([data-loaded])');
    lazyNativeImages.forEach(img => {
      img.setAttribute('data-loaded', 'true');
      // Browser will handle native lazy loading, but we mark it as processed
    });
  }

  /**
   * Reveal section (show interface) and trigger lazy-load event.
   * Section appears when scrolled into view (CSS-ready); Swiper/JS init runs in background.
   */
  function initSectionLazyLoad(section) {
    lazyLoadSectionImages(section);

    function reveal() {
      section.classList.remove('lazy-load');
      section.classList.add('lazy-loaded');
      section.style.opacity = '1';
      section.style.visibility = 'visible';
    }

    // Fire event so Swiper and other JS can initialize in the background
    section.dispatchEvent(new CustomEvent('section:lazy-load', { bubbles: true }));

    // Show interface on next frame – don't wait for JS. CSS is ready; sliders will init in background.
    requestAnimationFrame(reveal);
  }

  /**
   * Setup IntersectionObserver for section lazy loading
   * Shows section when it's 25% visible (previous section is 75% scrolled through)
   */
  function setupSectionLazyLoad() {
    // Find all sections marked for lazy loading
    const lazySections = document.querySelectorAll('section[data-lazy-load], .section[data-lazy-load], [data-section-lazy]');
    
    if (lazySections.length === 0) return;

    // Create observer that triggers when section is 25% visible
    // This means the previous section is 75% scrolled through
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // When 25% of the lazy section is visible, show it immediately (no fade)
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          initSectionLazyLoad(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px',
      threshold: [0.25] // Trigger at exactly 25% visibility
    });

    // Observe all lazy sections
    lazySections.forEach(section => {
      observer.observe(section);
    });
  }

  /**
   * Preload next section when current section is 50% visible
   */
  function setupSectionPreload() {
    const allSections = document.querySelectorAll('section, .section, [data-section]');
    
    if (allSections.length === 0) return;

    const preloadObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const currentIndex = Array.from(allSections).indexOf(entry.target);
          const nextSection = allSections[currentIndex + 1];
          
          if (nextSection && nextSection.hasAttribute('data-lazy-load')) {
            // Preload images in next section
            const nextImages = nextSection.querySelectorAll('img[data-src]');
            nextImages.forEach(img => {
              if (img.dataset.src) {
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = img.dataset.src;
                link.fetchPriority = 'low';
                document.head.appendChild(link);
              }
            });
          }
        }
      });
    }, {
      rootMargin: '0px',
      threshold: [0.5]
    });

    allSections.forEach(section => {
      preloadObserver.observe(section);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupSectionLazyLoad();
      setupSectionPreload();
    });
  } else {
    setupSectionLazyLoad();
    setupSectionPreload();
  }

  // Re-initialize for dynamically added sections (AJAX navigation, etc.)
  if ('MutationObserver' in window) {
    const sectionObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && (node.matches('section[data-lazy-load], .section[data-lazy-load], [data-section-lazy]') || 
                node.querySelector('section[data-lazy-load], .section[data-lazy-load], [data-section-lazy]'))) {
              setupSectionLazyLoad();
            }
          }
        });
      });
    });

    sectionObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

